import { useMemo, type ReactElement } from "react";
import { ArrowRightLeft, ClipboardList, Plus, Search, Unlink } from "lucide-react";
import { Badge, Button, Stack, Surface, Text } from "@ux/primitives";
import { palette, space } from "@ux/tokens";
import { useViewport } from "@ux/hooks";
import { useAssetAssignments } from "../asset-assignments/hooks";
import { formatAssetDisplay } from "../assets/display";
import { useAssetEntries } from "../assets/hooks";
import { describeApiError } from "../common/errors";
import type { AssetAssignmentView } from "../asset-assignments/types";

export interface WorkspaceAssetsPanelProps {
    readonly workspaceId: string;
    readonly onAddNewAsset: () => void;
    readonly onAssignExistingAsset: () => void;
    readonly onReleaseAssignment: (assignment: AssetAssignmentView) => void;
    /**
     * Optional. When supplied, the panel renders an "Inventory mode"
     * CTA in its action row. Omitted by callers that are already inside
     * inventory mode (so they don't offer to re-enter the page they're
     * on) or that don't surface bulk capture for any other reason.
     *
     * Why it lives here, not in the workspace form's BottomActionBar:
     * inventory mode is bulk asset-capture, not a form-state action
     * (Save/Delete/Cancel). Putting it next to "Add new asset" /
     * "Assign existing" pairs it with its semantic siblings, frees the
     * phone action bar from cramped four-button overflow, and keeps
     * Save/Delete/Cancel honest as edits-only controls.
     */
    readonly onStartInventory?: () => void;
    /**
     * Optional. When supplied, the panel renders a "Reassign All" CTA that
     * asks the parent to open the move-everything-to-another-workspace
     * flow. The button is disabled while the workspace has no assets,
     * since there is nothing to move. Omitted by callers that don't offer
     * the flow (e.g. inventory mode).
     */
    readonly onReassignAll?: () => void;
    /**
     * True when the workspace itself is soft-deleted. The panel still
     * renders the historical assignments (they outlive their target) but
     * disables the CTAs — you can't assign to a deleted workspace.
     */
    readonly disabled: boolean;
}

/**
 * The workspace-anchored campaign workflow's centerpiece. Drops into the
 * workspace edit form below the audit panel and shows what is currently
 * assigned at this workspace, with two paths to add more:
 *
 *   1. **Add new asset** — opens the asset form in a workspace-anchored
 *      sub-mode. Save fires the atomic `POST /api/workspaces/{id}/assets`
 *      so the asset and its assignment land together.
 *   2. **Assign existing asset** — opens a picker dialog scoped to active
 *      assets, badged with their current location. Picking one triggers
 *      a release-then-create sequence to re-assign it here.
 *
 * Per-row Release uses the existing release endpoint untouched. The panel
 * is intentionally presentational: it emits intent (the three callbacks)
 * and lets the parent page own the navigation/dialog/mutation state, so
 * we keep one source of truth for which mode the right pane is in.
 */
export function WorkspaceAssetsPanel({
    workspaceId,
    onAddNewAsset,
    onAssignExistingAsset,
    onReleaseAssignment,
    onStartInventory,
    onReassignAll,
    disabled,
}: WorkspaceAssetsPanelProps): ReactElement {
    const { isPhone } = useViewport();
    const assignmentsQuery = useAssetAssignments({
        assetId: "",
        userId: "",
        workspaceId,
        targetKind: "workspace",
        openOnly: true,
    });

    const rows = useMemo(
        () => assignmentsQuery.data ?? [],
        [assignmentsQuery.data],
    );
    const assetIds = useMemo(() => rows.map((r) => r.assetId), [rows]);
    const { map: assetIndex } = useAssetEntries(assetIds);

    return (
        <Surface tone="raised" padding={4}>
            <Stack gap={3}>
                <Stack direction="row" align="center" gap={2} justify="space-between" wrap>
                    <Stack direction="row" align="center" gap={2}>
                        <Text role="eyebrow">Assets at this workspace</Text>
                        <Badge tone="neutral">{rows.length}</Badge>
                    </Stack>
                    {!isPhone ? (
                        <Stack direction="row" gap={2} wrap>
                            {/* Reading order: rare → bulk → primary, then
                                "Reassign All" trailing — it acts on the
                                whole list rather than adding to it. The
                                ghost variant for "Assign existing" calms
                                the hierarchy now that several actions live
                                here. */}
                            <Button
                                variant="ghost"
                                size="sm"
                                leadingIcon={<Search size={14} />}
                                onClick={onAssignExistingAsset}
                                disabled={disabled}
                            >
                                Assign existing
                            </Button>
                            {onStartInventory !== undefined ? (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    leadingIcon={<ClipboardList size={14} />}
                                    onClick={onStartInventory}
                                    disabled={disabled}
                                >
                                    Inventory mode
                                </Button>
                            ) : null}
                            <Button
                                variant="primary"
                                size="sm"
                                leadingIcon={<Plus size={14} />}
                                onClick={onAddNewAsset}
                                disabled={disabled}
                            >
                                Add new asset
                            </Button>
                            {onReassignAll !== undefined ? (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    leadingIcon={<ArrowRightLeft size={14} />}
                                    onClick={onReassignAll}
                                    disabled={disabled || rows.length === 0}
                                >
                                    Reassign All
                                </Button>
                            ) : null}
                        </Stack>
                    ) : null}
                </Stack>

                {assignmentsQuery.isPending ? (
                    <Text role="muted">Loading assets…</Text>
                ) : assignmentsQuery.isError ? (
                    <Text role="muted" color={palette.danger}>
                        Could not load assets — {describeApiError(assignmentsQuery.error)}
                    </Text>
                ) : rows.length === 0 ? (
                    <Text role="muted">
                        No assets assigned yet. Use the buttons {isPhone ? "below" : "above"} to add one.
                    </Text>
                ) : (
                    <Stack gap={2}>
                        {rows.map((row) => (
                            <AssignmentRow
                                key={row.id}
                                row={row}
                                label={formatAssetDisplay(
                                    assetIndex.get(row.assetId) ?? null,
                                    row.assetId,
                                )}
                                onRelease={() => onReleaseAssignment(row)}
                                disabled={disabled}
                            />
                        ))}
                    </Stack>
                )}

                {/* On phone the CTAs hoist to the bottom of the panel rather than
                    the BottomActionBar — the page-level bar already owns the
                    workspace's Save/Cancel actions, and stacking another set of
                    buttons there would crowd the thumb zone. The same reading
                    order (rare → bulk → primary) as the desktop row is
                    preserved; `wrap` lets a 3-button row gracefully fall to
                    a second line on narrow phones rather than scrunching. */}
                {isPhone ? (
                    <Stack direction="row" gap={2} wrap>
                        <Button
                            variant="ghost"
                            leadingIcon={<Search size={14} />}
                            onClick={onAssignExistingAsset}
                            disabled={disabled}
                        >
                            Assign existing
                        </Button>
                        {onStartInventory !== undefined ? (
                            <Button
                                variant="secondary"
                                leadingIcon={<ClipboardList size={14} />}
                                onClick={onStartInventory}
                                disabled={disabled}
                            >
                                Inventory mode
                            </Button>
                        ) : null}
                        <Button
                            variant="primary"
                            leadingIcon={<Plus size={14} />}
                            onClick={onAddNewAsset}
                            disabled={disabled}
                        >
                            Add new asset
                        </Button>
                        {onReassignAll !== undefined ? (
                            <Button
                                variant="secondary"
                                leadingIcon={<ArrowRightLeft size={14} />}
                                onClick={onReassignAll}
                                disabled={disabled || rows.length === 0}
                            >
                                Reassign All
                            </Button>
                        ) : null}
                    </Stack>
                ) : null}
            </Stack>
        </Surface>
    );
}

interface AssignmentRowProps {
    readonly row: AssetAssignmentView;
    readonly label: string;
    readonly onRelease: () => void;
    readonly disabled: boolean;
}

function AssignmentRow({ row, label, onRelease, disabled }: AssignmentRowProps): ReactElement {
    return (
        <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap={2}
            style={{
                padding: `${space[2]} ${space[3]}`,
                background: palette.surface,
                border: `1px solid ${palette.border}`,
                borderRadius: 6,
            }}
        >
            <Text role="body">{label}</Text>
            <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Unlink size={14} />}
                onClick={onRelease}
                disabled={disabled || !row.isOpen}
            >
                Release
            </Button>
        </Stack>
    );
}
